import React from 'react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend 
} from 'recharts';
import { Box, Paper, Typography, Grid } from '@mui/material';
import { colors } from '../../styles/constants';

interface ChartContainerProps {
  title: string;
  children: React.ReactNode;
}

const ChartContainer: React.FC<ChartContainerProps> = ({ title, children }) => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 300,
        backgroundColor: colors.background.card,
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" component="div" sx={{ fontWeight: 'medium', mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ flexGrow: 1, width: '100%', height: '100%' }}>
        {children}
      </Box>
    </Paper>
  );
};

interface PieChartData {
  name: string;
  value: number;
  fill: string;
}

interface PieChartProps {
  data: PieChartData[];
  title: string;
}

export const CustomPieChart: React.FC<PieChartProps> = ({ data, title }) => {
  return (
    <ChartContainer title={title}>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => [`${value}`, 'Count']} />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface BarChartData {
  name: string;
  value: number;
  fill: string;
}

interface BarChartProps {
  data: BarChartData[];
  title: string;
}

export const CustomBarChart: React.FC<BarChartProps> = ({ data, title }) => {
  return (
    <ChartContainer title={title}>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip formatter={(value) => [`${value}`, 'Count']} />
          <Legend />
          <Bar dataKey="value" name="Count">
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface AnalyticsChartsProps {
  welcomePartyData: PieChartData[];
  weddingData: PieChartData[];
  transportationData: BarChartData[];
  relationshipData: PieChartData[];
  rsvpStatusData?: PieChartData[];
}

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({
  welcomePartyData,
  weddingData,
  transportationData,
  relationshipData,
  rsvpStatusData
}) => {
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={rsvpStatusData ? 4 : 6}>
        <CustomPieChart data={welcomePartyData} title="Welcome Party Attendance" />
      </Grid>
      <Grid item xs={12} md={rsvpStatusData ? 4 : 6}>
        <CustomPieChart data={weddingData} title="Wedding Attendance" />
      </Grid>
      {rsvpStatusData && (
        <Grid item xs={12} md={4}>
          <CustomPieChart data={rsvpStatusData} title="RSVP Status" />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <CustomBarChart data={transportationData} title="Transportation Options" />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomPieChart data={relationshipData} title="Guest Relationships" />
      </Grid>
    </Grid>
  );
};

export default AnalyticsCharts;
